import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { FieldType } from '@sla-ng/ui-material';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { MaterialPriceOptionEnum } from '../../../models/enum/material-price-option.enum';
import { MaterialQuantityOptionEnum } from '../../../models/enum/material-quantity-option.enum';
import { ProductionTimeOptionEnum } from '../../../models/enum/production-time-option.enum';
import { SalesPriceOptionEnum } from '../../../models/enum/sales-price-option.enum';
import { SalesVolumeOptionEnum } from '../../../models/enum/sales-volume-option.enum';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { ICostAccountingTypeState } from './cost-accounting-type-state.model';

let _state: ICostAccountingTypeState = {
    columns: [ 'name', 'material-price', 'material-quantity', 'production-time', 'sales-price', 'sales-volume', 'delete-button' ],
    dialogFields: [
        {
            field: 'name',
            type: FieldType.text,
            required: true,
            label: 'app.model.cost-accounting-type.name.label',
        },
        {
            field: 'materialQuantity',
            type: FieldType.select,
            required: true,
            label: 'app.model.cost-accounting-type.materialQuantity.label',
            selectData: {
                valueKey: 'value',
                labelKeys: [ 'label' ],
                options: of(
                    Object.values(MaterialQuantityOptionEnum)
                        .map(value => ({ value, label: `app.model.cost-accounting-type.materialQuantity.${value}.label` })),
                ),
            },
        },
        {
            field: 'materialPrice',
            type: FieldType.select,
            required: true,
            label: 'app.model.cost-accounting-type.materialPrice.label',
            selectData: {
                valueKey: 'value',
                labelKeys: [ 'label' ],
                options: of(
                    Object.values(MaterialPriceOptionEnum)
                        .map(value => ({ value, label: `app.model.cost-accounting-type.materialPrice.${value}.label` })),
                ),
            },
        },
        {
            field: 'productionTime',
            type: FieldType.select,
            required: true,
            label: 'app.model.cost-accounting-type.productionTime.label',
            selectData: {
                valueKey: 'value',
                labelKeys: [ 'label' ],
                options: of(
                    Object.values(ProductionTimeOptionEnum)
                        .map(value => ({ value, label: `app.model.cost-accounting-type.productionTime.${value}.label` }))),
            },
        },
        {
            field: 'salesVolume',
            type: FieldType.select,
            required: true,
            label: 'app.model.cost-accounting-type.salesVolume.label',
            selectData: {
                valueKey: 'value',
                labelKeys: [ 'label' ],
                options: of(
                    Object.values(SalesVolumeOptionEnum)
                        .map(value => ({ value, label: `app.model.cost-accounting-type.salesVolume.${value}.label` }))),
            },
        },
        {
            field: 'salesPrice',
            type: FieldType.select,
            required: true,
            label: 'app.model.cost-accounting-type.salesPrice.label',
            selectData: {
                valueKey: 'value',
                labelKeys: [ 'label' ],
                options: of(
                    Object.values(SalesPriceOptionEnum)
                        .map(value => ({ value, label: `app.model.cost-accounting-type.salesPrice.${value}.label` }))),
            },
        },
    ],
    isLoading: false,
};

@Injectable({
    providedIn: 'root',
})
export class CostAccountingTypeFacade {
    protected store$: BehaviorSubject<ICostAccountingTypeState> = new BehaviorSubject<ICostAccountingTypeState>(_state);

    constructor(private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myMessageService: MessageService) {
    }

    public subState(): Observable<ICostAccountingTypeState> {
        return this.store$.asObservable();
    }

    public async deleteCostAccountingType(costAccountintgTypeId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostAccountingTypeResource.deleteCostAccountingType(costAccountintgTypeId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-accounting-type.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }


    public async addCostAccountingType(costAccountingType: ICostAccountingType): Promise<void> {
        this.setIsloading(true);
        const createdType: ICostAccountingType = await this.myCostAccountingTypeResource.addCostAccountingType(costAccountingType).toPromise().catch(() => null);
        if (createdType !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-accounting-type.actions.added.label');
        }
        this.setIsloading(false);
    }

    private updateState(state: ICostAccountingTypeState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }
}
